import { useCallback, useEffect, useRef } from 'react';
import listeningMetricsService, {
  ListeningEventPayload,
  ListeningEventType
} from '../services/listeningMetricsService';
import listeningProgressService from '../services/listeningProgressService';
import { listeningEnv } from '../config/listeningEnv';
import { AudioTrack } from '../contexts/AudioPlayerContext';
import { Show } from '../types/Show';
import { debugLog, debugWarn } from '../utils/debugLog';
import checkoutAnalyticsService from '../services/checkoutAnalyticsService';

interface UseListeningTelemetryOptions {
  track: AudioTrack | null;
  isPlaying: boolean;
  currentTime: number;
}

interface SessionState {
  sessionId: string;
  sequence: number;
  startedAt: number;
  trackSnapshot: AudioTrack;
  maxProgress: number;
  lastHeartbeatPosition: number;
  startPosition: number;
  lastObservedProgress: number;
  listenedSeconds: number;
}

const DEFAULT_CLIENT_STREAM_TYPE = 'recorded';
const PROGRESS_PERSIST_INTERVAL_SECONDS = 5;
const MIN_PROGRESS_FOR_STOP_SECONDS = 5;

const generateSessionId = (): string => {
  const candidate = listeningMetricsService.createSessionId?.();
  if (typeof candidate === 'string' && candidate.trim().length > 0) {
    return candidate;
  }
  return `listen_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const resolveShowDuration = (track: AudioTrack): number | undefined => {
  const show = track.show;
  if (!show) {
    return track.duration;
  }

  if (typeof show.selected_length_seconds === 'number') {
    return show.selected_length_seconds;
  }

  if (typeof show.duration === 'number') {
    return show.duration;
  }

  return track.duration;
};

const resolveShowSlug = (show: Show | undefined): string | undefined => {
  if (!show) {
    return undefined;
  }

  if (show.presenter?.wordpress_slug) {
    return show.presenter.wordpress_slug;
  }

  const showId = show.show_id ?? '';
  if (showId.includes('#')) {
    return showId.split('#')[0];
  }

  return showId || undefined;
};

const buildEventPayload = (
  eventType: ListeningEventType,
  track: AudioTrack,
  sessionId: string,
  playbackPositionSeconds: number,
  listenSeconds: number,
  sequence: number
): ListeningEventPayload => {
  const durationSeconds = resolveShowDuration(track);

  return {
    sessionId,
    timestamp: new Date().toISOString(),
    showId: track.show?.show_id ?? undefined,
    showSlug: resolveShowSlug(track.show),
    showTitle: track.show?.show ?? track.title ?? undefined,
    presenterName: track.show?.presenter?.name ?? track.artist ?? undefined,
    trackId: track.id,
    trackTitle: track.title,
    streamType: track.isLive ? 'live' : DEFAULT_CLIENT_STREAM_TYPE,
    playbackPositionSeconds,
    listenSeconds,
    durationSeconds,
    sequence,
    metadata: {
      artist: track.artist,
      resumeFrom: track.resumeFrom ?? 0,
      environment: listeningEnv.environment,
      source: 'GlobalAudioPlayer'
    }
  };
};

const persistProgress = (track: AudioTrack, positionSeconds: number) => {
  if (!track.show?.show_id) {
    return;
  }

  const durationSeconds = resolveShowDuration(track);
  listeningProgressService.saveProgress(track.show.show_id, positionSeconds, durationSeconds);
};

const cleanupProgressIfComplete = (track: AudioTrack, positionSeconds: number) => {
  if (!track.show?.show_id) {
    return;
  }

  const durationSeconds = resolveShowDuration(track);
  listeningProgressService.pruneIfComplete(track.show.show_id, positionSeconds, durationSeconds);
};

const sanitizeProgress = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return value < 0 ? 0 : value;
};

const prepareSessionForProgress = (session: SessionState, progressSeconds: number): number => {
  const sanitizedProgress = sanitizeProgress(progressSeconds);
  const delta = sanitizedProgress - session.lastObservedProgress;

  if (delta > 0) {
    session.listenedSeconds += delta;
  }

  if (sanitizedProgress > session.maxProgress) {
    session.maxProgress = sanitizedProgress;
  }

  session.lastObservedProgress = sanitizedProgress;

  return sanitizedProgress;
};

export const useListeningTelemetry = ({
  track,
  isPlaying,
  currentTime
}: UseListeningTelemetryOptions): void => {
  const sessionRef = useRef<SessionState | null>(null);
  const previousTrackIdRef = useRef<string | null>(null);
  const lastPersistedProgressRef = useRef<number>(0);

  const dispatchEvent = useCallback(
    (eventType: ListeningEventType, session: SessionState, progressSeconds: number) => {
      const sanitizedProgress = prepareSessionForProgress(session, progressSeconds);
      const effectiveProgress =
        eventType === 'stop' ? session.maxProgress : sanitizedProgress;

      const payload = buildEventPayload(
        eventType,
        session.trackSnapshot,
        session.sessionId,
        Math.floor(effectiveProgress),
        Math.floor(session.listenedSeconds),
        session.sequence
      );

      session.sequence += 1;
      session.lastHeartbeatPosition = effectiveProgress;

      void listeningMetricsService.sendEvent(eventType, payload);
      checkoutAnalyticsService.trackShowListeningEvent(eventType, {
        sessionId: payload.sessionId,
        showId: payload.showId,
        showSlug: payload.showSlug,
        showTitle: payload.showTitle,
        presenterName: payload.presenterName,
        trackId: payload.trackId,
        trackTitle: payload.trackTitle,
        streamType: payload.streamType,
        playbackPositionSeconds: payload.playbackPositionSeconds,
        listenSeconds: payload.listenSeconds,
        durationSeconds: payload.durationSeconds,
        sequence: payload.sequence
      });
    },
    []
  );

  const endSession = useCallback(
    (reason: string, session: SessionState | null, progressSeconds: number) => {
      if (!session) {
        return;
      }

      const elapsedMs = Date.now() - session.startedAt;
      const sanitizedProgress = prepareSessionForProgress(session, progressSeconds);
      const hasMeaningfulProgress =
        session.listenedSeconds > 0 || sanitizedProgress > session.startPosition;

      if (
        reason === 'playback-paused' &&
        session.listenedSeconds < MIN_PROGRESS_FOR_STOP_SECONDS &&
        elapsedMs < MIN_PROGRESS_FOR_STOP_SECONDS * 1000
      ) {
        return;
      }

      if (!hasMeaningfulProgress) {
        sessionRef.current = null;
        previousTrackIdRef.current = null;
        debugLog?.('Listening telemetry session closed without progress', {
          reason,
          sessionId: session.sessionId
        });
        return;
      }

      cleanupProgressIfComplete(session.trackSnapshot, sanitizedProgress);

      dispatchEvent('stop', session, sanitizedProgress);
      sessionRef.current = null;
      previousTrackIdRef.current = null;
      if (session.sessionId) {
        debugWarn?.('Listening telemetry session closed', { reason, sessionId: session.sessionId });
      }
    },
    [dispatchEvent]
  );

  // Track changes: end previous session before switching tracks
  useEffect(() => {
    const currentTrackId = track && !track.isLive ? track.id : null;

    if (previousTrackIdRef.current && previousTrackIdRef.current !== currentTrackId) {
      const activeSession = sessionRef.current;
      if (activeSession) {
        endSession('track-changed', activeSession, currentTime);
      }
    }

    previousTrackIdRef.current = currentTrackId;

    if (!track || track.isLive) {
      if (sessionRef.current) {
        endSession('track-ended', sessionRef.current, currentTime);
      }
      return;
    }
  }, [track, currentTime, endSession]);

  // Handle playback start
  useEffect(() => {
    if (!track || track.isLive) {
      return;
    }

    if (!isPlaying) {
      return;
    }

    if (sessionRef.current) {
      return;
    }

    const session = {
      sessionId: generateSessionId(),
      sequence: 1,
      startedAt: Date.now(),
      maxProgress: Math.max(0, track.resumeFrom ?? 0),
      lastHeartbeatPosition: track.resumeFrom ?? 0,
      startPosition: Math.max(0, track.resumeFrom ?? 0),
      lastObservedProgress: Math.max(0, track.resumeFrom ?? 0),
      listenedSeconds: 0,
      trackSnapshot: track
    };

    sessionRef.current = session;
    dispatchEvent('start', session, track.resumeFrom ?? 0);
    lastPersistedProgressRef.current = track.resumeFrom ?? 0;

    if (track.show?.show_id && (track.resumeFrom ?? 0) > 0) {
      listeningProgressService.saveProgress(track.show.show_id, track.resumeFrom ?? 0, resolveShowDuration(track));
    }
  }, [isPlaying, track, dispatchEvent]);

  // Persist progress and send heartbeat events based on playback progress
  useEffect(() => {
    const session = sessionRef.current;

    if (!session || !track || track.isLive) {
      return;
    }

    const currentProgress = sanitizeProgress(currentTime);
    prepareSessionForProgress(session, currentProgress);
    const progressDelta = Math.abs(currentProgress - lastPersistedProgressRef.current);

    if (progressDelta >= PROGRESS_PERSIST_INTERVAL_SECONDS) {
      persistProgress(track, currentProgress);
      lastPersistedProgressRef.current = currentProgress;
    }

    const heartbeatInterval = listeningEnv.heartbeatIntervalSeconds;
    if (
      isPlaying &&
      heartbeatInterval > 0 &&
      currentProgress - session.lastHeartbeatPosition >= heartbeatInterval
    ) {
      debugLog?.('Listening telemetry heartbeat threshold reached', {
        currentProgress,
        lastHeartbeatPosition: session.lastHeartbeatPosition,
        listenedSeconds: session.listenedSeconds,
        heartbeatInterval
      });
      dispatchEvent('heartbeat', session, currentProgress);
    }

    session.maxProgress = Math.max(session.maxProgress, currentProgress);
  }, [currentTime, track, isPlaying, dispatchEvent]);

  // Handle playback stop / pause
  useEffect(() => {
    if (isPlaying) {
      return;
    }

    const session = sessionRef.current;
    if (!session) {
      return;
    }

    endSession('playback-paused', session, currentTime);
  }, [isPlaying, currentTime, endSession]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const session = sessionRef.current;
      if (session) {
        endSession('component-unmount', session, session.lastObservedProgress);
      }
    };
  }, [endSession]);
};

export const __TESTING__ = {
  prepareSessionForProgress,
  sanitizeProgress
};
